<template>
  <div class="settings-page">
    <h2>Logs</h2>
    <div class="subtitle">
      Configure log filtering for app, dashboard, and menubar.
    </div>

    <BaseTabs
      v-if="in_electron"
      v-model:selected-tab="selectedComponent"
      :tabs-titles="componentTabs"
      class="component-tabs"
    />

    <section v-if="selectedComponent !== 'Controller'" class="log-filters">
      <div class="filters-list">
        <div
          v-for="(level, module) in currentFilters"
          :key="module"
          class="filter-tag"
        >
          <span class="filter-label"
            >{{ module }}: {{ level.toUpperCase() }}</span
          >
          <button class="filter-remove" @click="removeFilter(String(module))">
            <CloseIcon />
          </button>
        </div>
        <div class="filter-form-inline">
          <BaseInput
            v-model:value="newFilterModule"
            placeholder="Module name"
            class="filter-input-inline"
          />
          <BaseInputSelect
            v-model:value="newFilterLevel"
            :options="levelOptions"
            placeholder="Level"
            class="filter-select-inline"
          />
          <BaseButton kind="auxiliary" type="primary" @click="addFilter">
            <template #icon>
              <PlusIcon />
            </template>
          </BaseButton>
        </div>
      </div>
    </section>

    <section class="log-display">
      <textarea
        :value="selectedLogContent"
        readonly
        class="log-textarea sentry-block"
      ></textarea>
      <div class="log-actions">
        <BaseButton
          kind="auxiliary"
          type="secondary"
          text="Download"
          @click="downloadLogs"
        />
        <BaseButton
          kind="auxiliary"
          type="secondary"
          text="Clear"
          :state="selectedComponent === 'Controller' ? 'disabled' : 'default'"
          @click="clearLogs"
        />
      </div>
    </section>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, watch } from "vue";

import BaseButton from "@/components/base/BaseButton.vue";
import BaseInput from "@/components/base/BaseInput.vue";
import BaseInputSelect from "@/components/base/BaseInputSelect.vue";
import BaseTabs from "@/components/base/BaseTabs.vue";
import CloseIcon from "@/components/icons/CloseIcon.vue";
import PlusIcon from "@/components/icons/PlusIcon.vue";
import * as inject from "@/components/inject";
import { logger as _logger } from "@/logger";
import { IDENTIFIERS, IPC_INTERFACE } from "@/logger/constants";
import { isLevel, levelOptions } from "@/logger/levels";
import { logger as rendererLogger } from "@/logger/renderer";
import type { Logger } from "@/logger/renderer";
import { entriesToText, textToEntries } from "@/logger/utils";
import { in_electron } from "@/utils/in";

export default defineComponent({
  components: {
    BaseButton,
    BaseInput,
    BaseInputSelect,
    BaseTabs,
    PlusIcon,
    CloseIcon,
  },
  setup() {
    const controller = inject.controller();

    // Component tab management
    const componentTabs = in_electron
      ? ["App", "Dashboard", "Menubar", "Controller"]
      : ["App"];
    const selectedComponent = ref<string>(componentTabs[0]);

    const currentFilters = ref<Logger.Filters>({});
    const newFilterModule = ref<string>("");
    const newFilterLevel = ref<string>("");

    // Load filters when component changes
    const loadFilters = async () => {
      if (selectedComponent.value === "App") {
        // Create a new object reference to ensure Vue reactivity
        currentFilters.value = { ..._logger.app.filter.get() };
      } else if (selectedComponent.value === "Dashboard") {
        const filters = await _logger.satellite.filter.get("dashboard");
        currentFilters.value = filters;
      } else if (selectedComponent.value === "Menubar") {
        const filters = await _logger.satellite.filter.get("menubar");
        currentFilters.value = filters;
      } else if (selectedComponent.value === "Controller") {
        // Controller logs don't support filters.
        currentFilters.value = {};
      }
    };

    watch(selectedComponent, loadFilters, { immediate: true });

    // Load logs when component changes
    const selectedLogContent = ref<string>("");
    const loadLogs = async () => {
      try {
        // Avoid rendering unbounded log volumes in the UI (can be very large for controller logs).
        // Full logs remain available via "Download".
        const DISPLAY_ENTRIES = 1000;
        const allLogs = await _logger.app.collect(controller, DISPLAY_ENTRIES);
        let identifier: string;
        if (selectedComponent.value === "App") {
          identifier = IDENTIFIERS.APP;
        } else if (selectedComponent.value === "Dashboard") {
          identifier = IDENTIFIERS.DASHBOARD;
        } else if (selectedComponent.value === "Menubar") {
          identifier = IDENTIFIERS.MENUBAR;
        } else if (selectedComponent.value === "Controller") {
          identifier = IDENTIFIERS.CONTROLLER;
        } else {
          identifier = IDENTIFIERS.APP;
        }
        const logText = allLogs[identifier] || "";
        if (!logText || logText.trim() === "") {
          selectedLogContent.value = "(no logs available)";
        } else {
          switch (selectedComponent.value) {
            case "Controller": {
              selectedLogContent.value = logText;
              break;
            }
            default: {
              const entries = textToEntries(logText);
              if (entries.length === 0) {
                selectedLogContent.value = logText;
                break;
              }
              const reversedEntries = [...entries].reverse();
              selectedLogContent.value = entriesToText(reversedEntries);
              break;
            }
          }
        }
      } catch (e) {
        window.console.error("Error loading logs:", e);
        selectedLogContent.value = `Error loading logs: ${e}`;
      }
    };

    watch(selectedComponent, loadLogs, { immediate: true });

    const addFilter = async () => {
      if (!newFilterModule.value || !isLevel(newFilterLevel.value)) {
        controller.notifications.notify("Please fill in all fields");
        return;
      }
      const filter: Logger.Filter = {
        module: newFilterModule.value,
        level: newFilterLevel.value,
      };

      if (selectedComponent.value === "App") {
        _logger.app.filter.set(filter.module, filter.level);
      } else if (selectedComponent.value === "Dashboard") {
        if (in_electron) {
          try {
            const { ipcRenderer } = require("electron");
            await ipcRenderer.invoke(
              IPC_INTERFACE.SET_FILTER,
              IDENTIFIERS.DASHBOARD,
              filter.module,
              filter.level
            );
          } catch {
            controller.notifications.notify("Failed to set filter");
            return;
          }
        }
      } else if (selectedComponent.value === "Menubar") {
        if (in_electron) {
          try {
            const { ipcRenderer } = require("electron");
            await ipcRenderer.invoke(
              IPC_INTERFACE.SET_FILTER,
              IDENTIFIERS.MENUBAR,
              filter.module,
              filter.level
            );
          } catch {
            controller.notifications.notify("Failed to set filter");
            return;
          }
        }
      }

      await loadFilters(); // Reload filters to show the new one
      newFilterModule.value = "";
      newFilterLevel.value = "";
    };

    const removeFilter = async (module: string) => {
      if (module) {
        try {
          if (selectedComponent.value === "App") {
            _logger.app.filter.unset(module);
          } else if (selectedComponent.value === "Dashboard") {
            await _logger.satellite.filter.unset("dashboard", module);
          } else if (selectedComponent.value === "Menubar") {
            await _logger.satellite.filter.unset("menubar", module);
          }
          await loadFilters(); // Reload filters to show the removal
        } catch (e) {
          window.console.error("Error removing filter:", e);
        }
      }
    };

    const downloadLogs = async () => {
      try {
        // Always re-collect to get the latest logs.
        const allLogs = await _logger.app.collect(controller);
        let identifier: string;
        if (selectedComponent.value === "App") {
          identifier = IDENTIFIERS.APP;
        } else if (selectedComponent.value === "Dashboard") {
          identifier = IDENTIFIERS.DASHBOARD;
        } else if (selectedComponent.value === "Menubar") {
          identifier = IDENTIFIERS.MENUBAR;
        } else if (selectedComponent.value === "Controller") {
          identifier = IDENTIFIERS.CONTROLLER;
        } else {
          identifier = IDENTIFIERS.APP;
        }

        const logText = allLogs[identifier] || "";
        if (!logText || logText.trim() === "") {
          window.console.error("No logs to download");
          return;
        }

        const blob = new Blob([logText], { type: "text/plain" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;

        // Generate filename based on component and timestamp
        const timestamp = new Date()
          .toISOString()
          .replace(/:/g, "-")
          .split(".")[0];
        const componentName = selectedComponent.value.toLowerCase();
        link.download = `${componentName}-${timestamp}.logs.txt`;

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      } catch (e) {
        window.console.error("Error downloading logs:", e);
      }
    };

    const clearLogs = async () => {
      try {
        if (selectedComponent.value === "App") {
          rendererLogger.renderer.clear();
        } else if (selectedComponent.value === "Dashboard") {
          await _logger.satellite.clear("dashboard");
        } else if (selectedComponent.value === "Menubar") {
          await _logger.satellite.clear("menubar");
        } else if (selectedComponent.value === "Controller") {
          // Controller logs can't be cleared from the renderer.
          return;
        }
        await loadLogs(); // Reload logs to show they're cleared
      } catch (e) {
        window.console.error("Error clearing logs:", e);
      }
    };

    return {
      componentTabs,
      currentFilters,
      in_electron,
      levelOptions,
      newFilterModule,
      newFilterLevel,
      selectedComponent,
      selectedLogContent,
      addFilter,
      removeFilter,
      downloadLogs,
      clearLogs,
    };
  },
});
</script>

<style scoped lang="scss">
@use "../../styles/settings.scss";

.component-tabs {
  margin-top: 24px;
  margin-bottom: 16px;
}

.log-filters {
  margin-top: 24px;

  h3 {
    @extend %font-body-regular;
    font-weight: 600;
    margin-bottom: 12px;
  }

  .filters-list {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    gap: 8px;
    align-items: center;

    .filter-tag {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      padding-block: 4px;
      padding-inline-end: 8px;
      padding-inline-start: 12px;
      border-radius: 16px;
      @include themed() {
        background-color: t($bg-menu-hover);
      }

      .filter-label {
        @extend %font-caption-regular;
        white-space: nowrap;
      }

      .filter-remove {
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 0;
        margin: 0;
        border: none;
        background: none;
        width: 16px;
        height: 16px;
        border-radius: 50%;
        @include themed() {
          color: t($primary);

          &:hover {
            background-color: t($bg-icon-menu-active);
          }
        }

        svg {
          width: 12px;
          height: 12px;
        }
      }
    }

    .filter-form-inline {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      padding-block: 4px;
      padding-inline-end: 8px;
      padding-inline-start: 12px;
      border-radius: 16px;
      @include themed() {
        background-color: t($bg-menu-hover);
      }

      .filter-input-inline {
        width: 120px;
        min-width: 0;
        height: 19px;

        :deep(input) {
          padding: 0;
          margin: 0;
          font-size: 13px;
          line-height: 19px;
          height: 19px;
          border: none;
          @extend %font-caption-regular;
          @include themed() {
            background-color: transparent;
            color: t($primary);
          }
        }
      }

      .filter-select-inline {
        width: 80px;
        min-width: 0;
        height: 19px;

        :deep(select) {
          padding: 0;
          margin: 0;
          font-size: 13px;
          line-height: 19px;
          height: 19px;
          border: none;
          @extend %font-caption-regular;
          @include themed() {
            background-color: transparent;
            color: t($primary);
          }
        }
      }

      :deep(button) {
        padding: 0;
        margin: 0;
        min-width: 16px;
        width: 16px;
        height: 16px;
        border: none;
        @include themed() {
          background-color: transparent;
          color: t($primary);

          &:hover {
            background-color: t($bg-icon-menu-active);
          }
        }
      }
    }
  }
}

.log-display {
  margin-top: 24px;

  .log-textarea {
    width: 100%;
    min-height: 300px;
    font-family: $mono;
    font-size: 12px;
    padding: 8px;
    border: 1px solid;
    border-radius: 4px;
    resize: vertical;
    @include themed() {
      background-color: t($bg);
      border-color: t($grey-20);
      color: t($primary);
    }
  }

  .log-actions {
    display: flex;
    flex-direction: row;
    gap: 8px;
    justify-content: flex-end;
    margin-top: 12px;
  }
}
</style>
